// Copyright (c) Docugami, Inc. All rights reserved.

import { ICommand } from "components/cards/CommandButton";
import { primaryTheme, warningRed, greyText } from "components/shell/presentation/AppStyles";
import { useDgIcons, dgIconNames } from "hooks/dgIcons";
import styled, { css } from "styled-components";

const ToggledCss = css`
    fill: ${primaryTheme.palette.white};
    background: ${warningRed};
    :hover {
        background: ${`${warningRed}A6`};
        :active {
            background: ${`${warningRed}E6`};
        }
    }
`;

interface ToggleItemContainerProps {
    isToggled: boolean;
    disabled: boolean;
    background: string;
}

const EnabledCss = css<ToggleItemContainerProps>`
    :hover {
        background: ${props => `${props.background}66`};
        fill: ${primaryTheme.palette.white};
        :active {
            background: ${props => `${props.background}E6`};
        }
    }
`;

const DisabledCss = css<ToggleItemContainerProps>`
    cursor: default;
    opacity: 0.5;
`;

const ToggleItemContainer = styled.div<ToggleItemContainerProps>`
    width: 14px;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px 4px;
    fill: ${greyText};
    ${props => !props.disabled ? EnabledCss : DisabledCss};
    ${props => !!props.isToggled && ToggledCss};
`;

const FlagIconContainer = styled.div`
    display: flex;
    height: 100%;
    font-size: 7.5px;
    align-items: center;
    justify-content: center;
`;

export enum ToggleType {
    Flag = 'flag'
}

export interface ToggleCommand {
    command: ICommand;
    isToggled: boolean;
    type: ToggleType;
}

interface ToggleCommandBarProps {
    toggles?: ToggleCommand[]
    hidden?: boolean;
}

export const ToggleCommandBar = (props: ToggleCommandBarProps) => {
    if (!props.toggles?.length) {
        return null;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {
                props.toggles.map(x => (<ToggleItem key={x.type} toggle={x} />))
            }
        </div>
    )
}

interface ToggleItemProps {
    toggle: ToggleCommand;
}

const ToggleItem = (props: ToggleItemProps) => {
    const icons = useDgIcons();

    const getIcon = () => {
        switch (props.toggle.type) {
            case ToggleType.Flag:
                return icons.icon(dgIconNames.DgDiligenceFlag, 'Flag', 9, 7.7, 'inherit', '');
            default:
                return null;
        }
    }

    return (
        <ToggleItemContainer
            isToggled={props.toggle.isToggled}
            disabled={!props.toggle.command.canExecute()}
            background={warningRed}
            onClick={props.toggle.command.canExecute() ? props.toggle.command.execute : undefined}>
            <FlagIconContainer>
                {getIcon()}
            </FlagIconContainer>
        </ToggleItemContainer>
    )
}
